import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  app_connections_path,
  app_design_path,
  app_drafts_path,
  app_publication_path,
} from '@/rails/routes';
import type { AppShell, Connection } from '@/types/platform';
import { Link } from '@inertiajs/react';
import { Circle, CircleCheck } from 'lucide-react';

interface NextStepsCardProps {
  app: AppShell;
  listCount: number;
  schemaCurrent: boolean;
  readyDrafts: number;
  // True while a described design is still being written.
  drafting: boolean;
  connections: Connection[];
  // True while setup is waiting or running. Setup applies the design itself.
  settingUp: boolean;
}

interface Step {
  key: string;
  title: string;
  detail: string;
  done: boolean;
  // Where to go to get it done. Left out when there is nothing for the owner to do.
  link?: { href: string; label: string };
}

// What is left before the app is designed, taking payments, and public. Worked out from the page's props.
const NextStepsCard = ({
  app,
  listCount,
  schemaCurrent,
  readyDrafts,
  drafting,
  connections,
  settingUp,
}: NextStepsCardProps) => {
  const hasLists = listCount > 0;
  const setupWillApply = settingUp && hasLists;
  const paymentAccount = connections.find(
    (connection) =>
      (connection.provider === 'stripe' || connection.provider === 'square') &&
      connection.status === 'connected'
  );

  const steps: Step[] = [
    {
      key: 'design',
      title: 'Finish the design',
      done: hasLists,
      detail: hasLists
        ? `${listCount} ${listCount === 1 ? 'list' : 'lists'} in the design.`
        : readyDrafts > 0
          ? `${readyDrafts} ${readyDrafts === 1 ? 'draft is' : 'drafts are'} ready to review.`
          : drafting
            ? 'Your design is being drafted.'
            : 'Your app has no lists yet.',
      link: {
        href: app_drafts_path(app.id),
        label: readyDrafts > 0 ? 'Review the draft' : 'Design with AI',
      },
    },
    {
      key: 'apply',
      title: 'Apply the design',
      done: schemaCurrent,
      detail: schemaCurrent
        ? 'Your app is running the current design.'
        : setupWillApply
          ? 'Setup applies the design for you.'
          : hasLists
            ? 'The design has changes your app is not running yet.'
            : 'Comes after the design has its first list.',
      link: setupWillApply
        ? undefined
        : { href: app_design_path(app.id), label: 'Open the design' },
    },
    {
      key: 'payments',
      title: 'Connect payments',
      done: Boolean(paymentAccount),
      detail: paymentAccount
        ? `${paymentAccount.label} is connected.`
        : 'Connect Stripe or Square so an invoice carries a pay link.',
      link: { href: app_connections_path(app.id), label: 'Connect an account' },
    },
    {
      key: 'publish',
      title: 'Publish',
      done: app.published,
      detail: app.published ? 'Your app is published.' : 'Give your app a public address.',
      link: { href: app_publication_path(app.id), label: 'Go to publish' },
    },
  ];
  const remaining = steps.filter((step) => !step.done).length;

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle>
          <h2>Next steps</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          {remaining === 0
            ? 'All done. Nothing is waiting on you.'
            : `${remaining} of ${steps.length} left.`}
        </p>
        <ul className="space-y-3">
          {steps.map((step) => (
            <li key={step.key} className="flex items-start gap-2">
              {step.done ? (
                <CircleCheck
                  className="mt-0.5 h-4 w-4 shrink-0 text-green-700"
                  aria-hidden="true"
                />
              ) : (
                <Circle
                  className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden="true"
                />
              )}
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="font-medium">{step.title}</p>
                <p className="text-muted-foreground">{step.detail}</p>
                {step.link && !step.done ? (
                  <Link href={step.link.href} className="inline-block font-medium underline">
                    {step.link.label}
                  </Link>
                ) : null}
              </div>
              <span className="text-muted-foreground shrink-0 text-xs">
                {step.done ? 'Done' : 'To do'}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default NextStepsCard;
